import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { LayoutScopeKey } from 'modules/manager/domain/match-layout';
import React from 'react';
import type { MessageDescriptor } from 'shared/i18n';

/**
 * Everything the «Раскладка …» and «Фильтрация …» sections' prose needs in order
 * to talk about one kind of slot.
 *
 * Russian inflects by gender and case, so a scope cannot be described by a
 * single noun — «строка» is feminine and «блок» is masculine, which is why the
 * copy this replaced had to branch even mid-word
 * («при одн{isStandings ? 'ой строке' : 'ом блоке'}»). Every form the copy uses
 * is its own field here, so the section bodies contain no ternaries at all and a
 * new scope is one new entry.
 *
 * Word forms are Lingui descriptors (module level, the language is not known
 * yet): the section translates each with `t(…)` and passes it into a whole
 * translated sentence as a placeholder.
 */
export interface LayoutScopeDescriptor {
  /** The `data.*` field addressing a slot. Must equal this entry's record key. */
  scopeKey: LayoutScopeKey;

  // — «Раскладка …» section
  /** InspectorSection title, e.g. «Раскладка матчей». */
  sectionTitle: MessageDescriptor;
  /** Nominative plural as subject/object: «пустые {unitPlural} скрываются». */
  unitPlural: MessageDescriptor;
  /** Genitive plural: «оставшееся от скрытых {unitPluralGen}». */
  unitPluralGen: MessageDescriptor;
  /** Capitalized nominative plural, sentence-initial: «{unitPluralCap} определяются…». */
  unitPluralCap: MessageDescriptor;
  /** Singular after a numeral 2-4: «минимум 2 оставшихся {unitCountedTwo}». */
  unitCountedTwo: MessageDescriptor;
  /** Prepositional singular including the numeral: «при {unitSingularPrep}». */
  unitSingularPrep: MessageDescriptor;
  /** «расположены {stackedAdverbial}»: «одна под другой» / «один под другим». */
  stackedAdverbial: MessageDescriptor;
  /** Where the slot number is read from, after «определяются автоматически по». */
  autoSource: MessageDescriptor;
  /**
   * Genitive plural counted by the preview field: «будто передано только N
   * {previewUnitGen}». Its own field rather than `unitPluralGen` because the
   * preview counts the *data* passed to the render (матчей, строк), not the
   * template's drawn bundles («блоков матчей»).
   */
  previewUnitGen: MessageDescriptor;
  /**
   * Section-level help. A whole node rather than word-parts: these paragraphs
   * are structurally different per scope (one argues about 8 group rows, one
   * about 6 match blocks), so word-level parameterization would be false economy.
   */
  sectionHelp: React.ReactNode;

  // — «Фильтрация …» section
  filterSectionTitle: MessageDescriptor;
  filterFieldLabel: MessageDescriptor;
  filterHelp: React.ReactNode;
}

/**
 * `Record<LayoutScopeKey, …>` on purpose — the same trick TEMPLATE_TYPE_REGISTRY
 * uses. Widening `LayoutScopeKey` is a compile error until a descriptor exists,
 * so a new scope can never reach the UI half-described.
 */
export const LAYOUT_SCOPE_DESCRIPTORS: Readonly<Record<LayoutScopeKey, LayoutScopeDescriptor>> = {
  matchNumber: {
    scopeKey: 'matchNumber',
    sectionTitle: msg`Раскладка матчей`,
    unitPlural: msg`блоки матчей`,
    unitPluralGen: msg`блоков матчей`,
    unitPluralCap: msg({ message: 'Блоки', context: 'match blocks, sentence start' }),
    unitCountedTwo: msg({ message: 'блока', context: 'match block after 2-4' }),
    unitSingularPrep: msg({ message: 'одном блоке', context: 'match block' }),
    stackedAdverbial: msg({ message: 'один под другим', context: 'match blocks' }),
    autoSource: msg`значению matchNumber у плейсхолдеров`,
    previewUnitGen: msg`матчей`,
    sectionHelp: (
      <span>
        <Trans>
          Шаблон рисуется с фиксированным числом блоков матчей (например, 6). Если реальных матчей меньше (например, 4), лишние блоки
          шаблона просто скрываются и не попадают в итоговое изображение. Эта настройка решает, что происходит с оставшимися видимыми
          блоками: остаться ровно там, где их разместили при вёрстке шаблона (тогда в области останется пустое место), или сдвинуться
          так, чтобы аккуратно заполнить область, где раньше были нарисованы все 6 блоков. На сами данные (какой матч в каком блоке)
          это не влияет — меняется только то, где блок нарисован на картинке.
        </Trans>
      </span>
    ),
    filterSectionTitle: msg`Фильтрация по матчу`,
    filterFieldLabel: msg`Матч №`,
    filterHelp: (
      <span>
        <Trans>
          Скрывает каждый выбранный объект, если матч N не рендерится. <code>1</code> = первый матч из /render, <code>2</code> = второй
          и т.д. Используется только для мульти-матч шаблонов (например, «Несколько матчей»).
        </Trans>
      </span>
    ),
  },
  teamNumber: {
    scopeKey: 'teamNumber',
    sectionTitle: msg`Раскладка строк`,
    unitPlural: msg({ message: 'строки', context: 'table rows, plural' }),
    unitPluralGen: msg({ message: 'строк', context: 'table rows, genitive' }),
    unitPluralCap: msg`Строки`,
    unitCountedTwo: msg({ message: 'строки', context: 'table row after 2-4' }),
    unitSingularPrep: msg`одной строке`,
    stackedAdverbial: msg`одна под другой`,
    autoSource: msg`значению «Позиция команды» у плейсхолдеров`,
    previewUnitGen: msg({ message: 'строк', context: 'table rows passed to the render' }),
    sectionHelp: (
      <span>
        <Trans>
          Шаблон турнирной таблицы рисуется с фиксированным числом строк (например, 8 — по числу мест в группе). Если в реальной группе
          команд меньше (например, 5), лишние строки шаблона просто скрываются и не попадают в итоговое изображение. Эта настройка
          решает, что происходит с оставшимися видимыми строками: остаться ровно там, где их разместили при вёрстке шаблона (тогда в
          области останется пустое место), или сдвинуться так, чтобы аккуратно заполнить область, где раньше были нарисованы все 8
          строк. На сами данные (какая команда на каком месте) это не влияет — меняется только то, где строка нарисована на картинке.
        </Trans>
      </span>
    ),
    filterSectionTitle: msg`Фильтрация по позиции`,
    filterFieldLabel: msg`Позиция команды`,
    filterHelp: (
      <span>
        <Trans>
          Привязывает выбранный объект к строке таблицы. <code>1</code> = первое место, <code>2</code> = второе и т.д. Если в группе
          меньше команд, чем строк в шаблоне, объекты лишних строк убираются при рендере. Используется только для шаблонов «Турнирная
          таблица».
        </Trans>
      </span>
    ),
  },
  playerNumber: {
    scopeKey: 'playerNumber',
    sectionTitle: msg`Раскладка игроков`,
    unitPlural: msg`блоки игроков`,
    unitPluralGen: msg`блоков игроков`,
    // «блок» is masculine — the same forms as матч, unlike the feminine «строка».
    unitPluralCap: msg({ message: 'Блоки', context: 'player blocks, sentence start' }),
    unitCountedTwo: msg({ message: 'блока', context: 'player block after 2-4' }),
    unitSingularPrep: msg({ message: 'одном блоке', context: 'player block' }),
    stackedAdverbial: msg({ message: 'один под другим', context: 'player blocks' }),
    autoSource: msg`значению «Игрок №» у плейсхолдеров`,
    previewUnitGen: msg`игроков`,
    sectionHelp: (
      <span>
        <Trans>
          Шаблон статистики игроков рисуется с фиксированным числом мест (например, 5 — топ-5). Если в рейтинге игроков меньше
          (например, 3) или в настройке «Ранжирование» задан меньший «Топ N», лишние блоки шаблона просто скрываются и не попадают в
          итоговое изображение. Эта настройка решает, что происходит с оставшимися видимыми блоками: остаться ровно там, где их
          разместили при вёрстке шаблона (тогда в области останется пустое место), или сдвинуться так, чтобы аккуратно заполнить
          область, где раньше были нарисованы все 5 блоков. На сами данные (какой игрок на каком месте) это не влияет — меняется только
          то, где блок нарисован на картинке.
        </Trans>
      </span>
    ),
    filterSectionTitle: msg`Фильтрация по месту в рейтинге`,
    filterFieldLabel: msg`Игрок №`,
    filterHelp: (
      <span>
        <Trans>
          Привязывает выбранный объект к месту в рейтинге. <code>1</code> = первое место, <code>2</code> = второе и т.д. Если игроков в
          рейтинге меньше, чем мест в шаблоне, объекты лишних мест убираются при рендере. Используется только для шаблонов «Статистика
          игроков».
        </Trans>
      </span>
    ),
  },
};
